import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import { IconButton, TextField } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { ClipboardIcon } from "@hugeicons-pro/core-stroke-rounded";
import { useCopyToClipboard } from "components/common/useCopyToClipboard";

interface SecretRevealDialogProps {
  open: boolean;
  title: string;
  // What the secret belongs to: a key's name, an endpoint's URL.
  subtitle?: string;
  body: string;
  secretLabel: string;
  secret: string | null;
  onClose: () => void;
}

/** Shows a just-issued secret, the only time it is ever readable. */
const SecretRevealDialog: React.FC<SecretRevealDialogProps> = ({
  open,
  title,
  subtitle,
  body,
  secretLabel,
  secret,
  onClose,
}) => {
  const { t } = useTranslation();
  const copy = useCopyToClipboard(t("Copied to clipboard"));

  const handleCopy = () => {
    if (!secret) return;
    copy(secret);
  };

  return (
    <Dialog
      open={open}
      // Force the customer to acknowledge: no onClose, so neither backdrop
      // click nor escape dismisses; the only close path is the button below.
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {title}
        {subtitle && (
          <Typography
            component="span"
            variant="body2"
            sx={{ display: "block", color: "text.secondary" }}
          >
            {subtitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" role="status" sx={{ mb: "var(--space-4)" }}>
          {t("SecretRevealWarning")}
        </Alert>
        <DialogContentText sx={{ mb: "var(--space-4)" }}>
          {body}
        </DialogContentText>
        <TextField
          // An explicit id keeps the DS out of React's useId, whose colons make
          // the generated rule an invalid selector once this sx nests one (R11).
          id="revealed-secret"
          aria-label={secretLabel}
          value={secret || ""}
          fullWidth
          multiline
          minRows={2}
          readOnly
          // A secret is read back character by character, and the DS ships no
          // mono family, so this one stays until it does.
          sx={{
            "& input, & textarea": {
              fontFamily: "monospace",
              fontSize: "var(--type-size-sm)",
            },
          }}
          trailing={
            <IconButton
              size="small"
              variant="ghost"
              aria-label={t("Copy")}
              onClick={handleCopy}
            >
              <HugeiconsIcon icon={ClipboardIcon} size={16} />
            </IconButton>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button variant="default-outlined" onClick={handleCopy}>
          {t("Copy")}
        </Button>
        <Button onClick={onClose} variant="primary-filled">
          {t("I've saved it")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SecretRevealDialog;
